import type { ManualSyncRequest } from "@adpilot/shared";
import { IconRefresh } from "@tabler/icons-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { TimeoutError } from "ky";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { workspaceSyncQueryKey } from "@/hooks/use-workspace-sync-status";
import type { AccountConnection } from "@/lib/amazon-api";
import { getApiErrorMessage } from "@/lib/api-error";
import { startManualSync } from "@/lib/manual-sync-api";
import { isValidManualSyncTimeZone } from "@/lib/manual-sync-range";
import { marketplaceDisplayName } from "./AmazonAuthorizationGuide";
import { ManualSyncDialog } from "./ManualSyncDialog";

interface ManualSyncActionProps {
  workspaceId: string;
  connections: readonly AccountConnection[];
  disabled?: boolean;
}

export function ManualSyncAction({
  workspaceId,
  connections,
  disabled = false,
}: ManualSyncActionProps) {
  const { t, i18n } = useTranslation("settings");
  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string>();
  const queryClient = useQueryClient();
  const mutation = useMutation({ mutationFn: startManualSync });
  const shops = connections.flatMap((connection) =>
    connection.shops
      .filter((shop) => shop.workspaceId === workspaceId)
      .map((shop) => ({
        shopId: shop.shopId,
        label: `${shop.shopName} · ${marketplaceDisplayName(shop.marketplaceName, i18n.language)}`,
        timeZone: isValidManualSyncTimeZone(shop.adsTimezone) ? shop.adsTimezone : "UTC",
        disabled:
          !shop.isActive || shop.status !== "connected" || connection.status !== "connected",
      })),
  );

  async function handleSubmit(input: ManualSyncRequest) {
    setSubmitError(undefined);
    try {
      const response = await mutation.mutateAsync(input);
      void queryClient.invalidateQueries({ queryKey: ["amazon"] });
      void queryClient.invalidateQueries({ queryKey: workspaceSyncQueryKey(workspaceId) });
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      const incomplete = response.results.flatMap((result) => {
        const sources = [
          result.ads.status === "failed" || result.ads.status === "busy"
            ? `${t("manualSync.ads")}：${t(`manualSync.${result.ads.status}Source`)}`
            : null,
          result.sales.status === "failed" || result.sales.status === "busy"
            ? `${t("manualSync.sales")}：${t(`manualSync.${result.sales.status}Source`)}`
            : null,
        ].filter(Boolean);
        if (!sources.length) return [];
        const label = shops.find((shop) => shop.shopId === result.shopId)?.label ?? result.shopId;
        return [`${label}（${sources.join(" / ")}）`];
      });
      if (incomplete.length) {
        setSubmitError(t("manualSync.partialFailed", { shops: incomplete.join("、") }));
        return;
      }
      const queued = response.results.some(
        (result) => result.ads.status === "queued" || result.sales.status === "queued",
      );
      if (!queued) {
        setSubmitError(t("manualSync.busy"));
        return;
      }
      setSubmitted(true);
      setOpen(false);
    } catch (error) {
      const message = await getApiErrorMessage(error);
      setSubmitError(message);
      if (error instanceof TypeError || error instanceof TimeoutError) toast.error(message);
    }
  }

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        disabled={disabled || shops.every((shop) => shop.disabled)}
        onClick={() => {
          setSubmitted(false);
          setSubmitError(undefined);
          setOpen(true);
        }}
      >
        <IconRefresh data-icon="inline-start" aria-hidden />
        {t("dataStatus.repairReports")}
      </Button>
      {submitted ? (
        <span role="status" className="text-xs text-muted-foreground">
          {t("manualSync.started")}
        </span>
      ) : null}
      <ManualSyncDialog
        open={open}
        onOpenChange={setOpen}
        shops={shops}
        isSubmitting={mutation.isPending}
        onSubmit={handleSubmit}
        submitError={submitError}
      />
    </>
  );
}
